// src/utils/NotificationService.js
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

export const NOTIF_TYPES = {
  // Supervisor/admin feed
  SERVICE_SUBMITTED:  'service_submitted',
  EMERGENCY_SERVICE:  'emergency_service',
  FAULT_RAISED:       'fault_raised',
  // Technician feed
  PHOTO_FLAGGED:      'photo_flagged',
  SUPERVISOR_MESSAGE: 'supervisor_message',
  SERVICE_REJECTED:   'service_rejected',
  ANNOUNCEMENT:       'announcement',
  JOURNEY_ASSIGNED:   'journey_assigned'
};

const ICONS = {
  service_submitted:  '🔧',
  emergency_service:  '🚨',
  fault_raised:       '⚠️',
  photo_flagged:      '🚩',
  supervisor_message: '💬',
  service_rejected:   '❌',
  announcement:       '📢',
  journey_assigned:   '🗺️'
};

const COLORS = {
  service_submitted:  '#6366f1',
  emergency_service:  '#ef4444',
  fault_raised:       '#ef4444',
  photo_flagged:      '#f59e0b',
  supervisor_message: '#06b6d4',
  service_rejected:   '#ef4444',
  announcement:       '#8b5cf6',
  journey_assigned:   '#10b981'
};

// ── Write to supervisor/admin feed ────────────────────────────────────────────
export const createNotification = async (type, title, message, meta = {}) => {
  try {
    await addDoc(collection(db, 'notifications'), {
      type,
      title,
      message,
      meta,
      icon:      ICONS[type]  || '📋',
      color:     COLORS[type] || '#6366f1',
      read:      false,
      createdAt: Timestamp.now()
    });
  } catch (err) {
    console.error('[Notifications] Failed to create supervisor notification:', err);
  }
};

// ── Write to technician feed ──────────────────────────────────────────────────
// Each doc has recipientId so technician only sees their own
export const createTechnicianNotification = async (
  recipientId, recipientName, type, title, message, meta = {}
) => {
  try {
    await addDoc(collection(db, 'technicianNotifications'), {
      recipientId,
      recipientName,
      type,
      title,
      message,
      meta,
      icon:      ICONS[type]  || '📋',
      color:     COLORS[type] || '#6366f1',
      read:      false,
      createdAt: Timestamp.now()
    });
  } catch (err) {
    console.error('[Notifications] Failed to create technician notification:', err);
  }
};

// ── Supervisor/admin helpers ──────────────────────────────────────────────────

export const notifyServiceSubmitted = (service, technicianName) =>
  createNotification(
    NOTIF_TYPES.SERVICE_SUBMITTED,
    'New Service Record',
    `${technicianName} completed a service at ${service.outletName}`,
    {
      serviceId:      service.id || null,
      orderNumber:    service.orderNumber,
      technicianName,
      outletName:     service.outletName,
      serviceType:    service.serviceType
    }
  );

export const notifyEmergencyService = (service, technicianName) =>
  createNotification(
    NOTIF_TYPES.EMERGENCY_SERVICE,
    '🚨 Emergency Service Reported',
    `${technicianName} reported an EMERGENCY at ${service.outletName}`,
    {
      serviceId:      service.id || null,
      orderNumber:    service.orderNumber,
      technicianName,
      outletName:     service.outletName
    }
  );

export const notifyFaultRaised = (technicianName, outletName, description, serviceId) =>
  createNotification(
    NOTIF_TYPES.FAULT_RAISED,
    '⚠️ Fault Report Raised',
    `${technicianName} raised a fault at ${outletName}: "${description}"`,
    { serviceId, technicianName, outletName, description }
  );

// ── Technician helpers ────────────────────────────────────────────────────────

// ✅ FIXED: Called when supervisor flags a photo — notifies the technician who submitted it
export const notifyPhotoFlagged = async (supervisor, service, photoType) => {
  try {
    // Get flag information from service
    const flagInfo = service.photoFlags?.[photoType];
    
    if (!flagInfo || !flagInfo.flagged) {
      console.error('No flag information found for photo:', photoType);
      return;
    }

    const flaggedBy = supervisor?.name || flagInfo.flaggedBy || 'Supervisor';
    const reason = flagInfo.reason || 'Photo needs review';
    
    // Notify supervisors/admins
    await createNotification(
      NOTIF_TYPES.PHOTO_FLAGGED,
      'Photo Flagged',
      `${flaggedBy} flagged a ${photoType} photo on service #${service.orderNumber || service.id?.slice(-6)} — "${reason}"`,
      {
        serviceId:   service.id || null,
        orderNumber: service.orderNumber || service.serviceRecordNumber || service.id?.slice(-6) || 'N/A',
        outletName:  service.outletName || 'Unknown',
        photoType,
        flaggedBy,
        reason
      }
    );
    
    // Also notify the technician who owns this service
    if (service.technicianId) {
      await createTechnicianNotification(
        service.technicianId,
        service.technicianName || 'Technician',
        NOTIF_TYPES.PHOTO_FLAGGED,
        '🚩 Photo Flagged on Your Service',
        `Your ${photoType} photo on service #${service.orderNumber || service.id?.slice(-6)} at ${service.outletName || 'Unknown'} was flagged by ${flaggedBy}: "${reason}"`,
        {
          serviceId:   service.id || null,
          orderNumber: service.orderNumber || service.serviceRecordNumber || service.id?.slice(-6) || 'N/A',
          outletName:  service.outletName || 'Unknown',
          photoType,
          flaggedBy,
          reason,
          action:      flagInfo.action || 'retake',
          actionLabel: flagInfo.action === 'retake' ? '📸 Retake Photo' : '📝 Provide More Info'
        }
      );
    }
    
    console.log('✅ Flag notification sent successfully to:', service.technicianName);
    return true;
    
  } catch (error) {
    console.error('❌ Error sending flag notification:', error);
    throw error;
  }
};

// Called when supervisor sends a message to a specific technician
export const notifySupervisorMessage = (
  recipientId, recipientName, senderName, message
) =>
  createTechnicianNotification(
    recipientId,
    recipientName,
    NOTIF_TYPES.SUPERVISOR_MESSAGE,
    `💬 Message from ${senderName}`,
    message,
    { senderName }
  );

// Called when supervisor rejects a service record
export const notifyServiceRejected = (
  recipientId, recipientName, orderNumber, outletName, reason, rejectedBy
) =>
  createTechnicianNotification(
    recipientId,
    recipientName,
    NOTIF_TYPES.SERVICE_REJECTED,
    '❌ Service Record Needs Correction',
    `Your service record #${orderNumber} at ${outletName} needs correction: "${reason}"`,
    { orderNumber, outletName, reason, rejectedBy }
  );

// Called when supervisor broadcasts to ALL technicians
export const notifyAnnouncement = async (techniciansList, senderName, message) => {
  // Write one doc per technician so each sees it in their own feed
  const promises = techniciansList.map(tech =>
    createTechnicianNotification(
      tech.id,
      tech.name,
      NOTIF_TYPES.ANNOUNCEMENT,
      `📢 Announcement from ${senderName}`,
      message,
      { senderName }
    )
  );
  await Promise.all(promises);
};

// Called when supervisor assigns a journey plan
export const notifyJourneyAssigned = (
  recipientId, recipientName, assignedBy, date, stopCount
) =>
  createTechnicianNotification(
    recipientId,
    recipientName,
    NOTIF_TYPES.JOURNEY_ASSIGNED,
    '🗺️ Journey Plan Assigned',
    `${assignedBy} assigned you a journey plan for ${date} with ${stopCount} stop${stopCount !== 1 ? 's' : ''}`,
    { assignedBy, date, stopCount }
  );
